<template>
  <div>
    <h4>Compare and Update Databases</h4>
  </div>
  <div class="row">
    <div class="col">
      <p>
        Ensure your Source DEV database is current with the Visual Studio
        database project. Use the comparison tool below to present the changes
        and create the update SQL script. When ready, 'Copy'
        this SQL script and apply to your databases.
      </p>
    </div>
  </div>
  <div class="row mb-2">
    <div class="col-6">
      <div class="btn-group" role="group">
        <input type="radio" class="btn-check" id="sourceDb" value="database" v-model="sourceMode" autocomplete="off" checked>
        <label class="btn btn-outline-primary btn-sm" for="sourceDb">Database</label>

        <input type="radio" class="btn-check" id="sourceFile" value="file" v-model="sourceMode" autocomplete="off">
        <label class="btn btn-outline-primary btn-sm" for="sourceFile">From File</label>
      </div>
    </div>
    <div class="col-6">
      <!-- Comparison mode toggle (shown after file upload) -->
      <div v-if="sourceMode === 'file' && tempDatabaseName" class="btn-group" role="group">
        <input type="radio" class="btn-check" id="singleMode" value="single" v-model="comparisonMode" autocomplete="off">
        <label class="btn btn-outline-primary btn-sm" for="singleMode">Single Target</label>

        <input type="radio" class="btn-check" id="bulkMode" value="bulk" v-model="comparisonMode" autocomplete="off">
        <label class="btn btn-outline-primary btn-sm" for="bulkMode">Bulk Comparison</label>
      </div>
    </div>
  </div>
  <div class="row">
    <div class="col">
      <!-- COMPARE -->
      <div class="input-group custom-group">
        <span class="input-group-text">Source</span>

        <!-- Database Mode -->
        <Multiselect v-if="sourceMode === 'database'" v-model="source" class="form-control custom-multiselect" placeholder="choose database"
          :open-direction="'bottom'" :closeOnSelect="true" :groups="true" :options="groupDevDBs" mode="single"
          label="target" value-prop="databaseName" :style="{ '--ms-max-height': '30rem' }" @clear="reset('source')" />

        <!-- File Upload Mode -->
        <div v-else class="form-control d-flex align-items-center">
          <input type="file" ref="fileInput" @change="handleFileUpload" accept=".zip" class="form-control-sm" :disabled="isUploading">
          <span v-if="isUploading" class="ms-2">
            <FontAwesomeIcon icon="fa-solid fa-spinner" spin /> Creating database...
          </span>
          <span v-else-if="uploadedFileName && tempDatabaseName" class="ms-2 text-success">
            <FontAwesomeIcon icon="fa-solid fa-check-circle" /> {{ uploadedFileName }}
          </span>
        </div>

        <!-- Target field (shown in single mode or database mode) -->
        <template v-if="sourceMode === 'database' || (sourceMode === 'file' && comparisonMode === 'single')">
          <span class="input-group-text">Target</span>
          <Multiselect v-model="target" class="form-control custom-multiselect" placeholder="choose database"
            :open-direction="'bottom'" :closeOnSelect="true" :groups="true" :options="groupTargetDBs"
            :disabled="!isSource" mode="single" label="target" value-prop="databaseName"
            :style="{ '--ms-max-height': '30rem' }" @clear="reset('target')" />
          <button class="btn btn-primary" :disabled="!selectedCompare || isComparing" @click="compareDBs">
            Compare
          </button>
          <button v-if="gotCompare" class="btn btn-primary custom-separator" @click="showFullScript">
            Show Script
          </button>
        </template>
      </div>
    </div>
  </div>

  <!-- Bulk Comparison Filters (shown in bulk mode) -->
  <div v-if="sourceMode === 'file' && tempDatabaseName && comparisonMode === 'bulk'" class="row mt-2">
    <div class="col">
      <div class="card">
        <div class="card-body">
          <h6 class="card-title mb-3">Bulk Comparison - Apply to Multiple Databases</h6>
          <div class="row g-3">
            <div class="col-md-4">
              <label class="form-label">Filter by Type</label>
              <select v-model="bulkType" class="form-select">
                <option :value="null">All Types</option>
                <option v-for="type in uniqueTypes" :key="type" :value="type">{{ type }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">Filter by Group</label>
              <select v-model="bulkGroup" class="form-select">
                <option :value="null">All Groups</option>
                <option v-for="group in uniqueGroups" :key="group" :value="group">{{ group }}</option>
              </select>
            </div>
            <div class="col-md-4">
              <label class="form-label">&nbsp;</label>
              <button class="btn btn-primary w-100" :disabled="isBulkComparing" @click="performBulkCompare">
                <FontAwesomeIcon v-if="isBulkComparing" icon="fa-solid fa-spinner me-2" spin />
                Compare All Matching Databases
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <!-- Bulk Comparison in Progress -->
  <div v-if="isBulkComparing" class="row mt-2">
    <div class="col">
      <FontAwesomeIcon icon="fa-solid fa-spinner me-2" spin /> Comparing against multiple databases...
    </div>
  </div>
  <!-- Bulk Comparison Results Table -->
  <div v-else-if="bulkResults.length > 0 && !reviewingBulkResult" class="row mt-3">
    <div class="col">
      <h5>Bulk Comparison Results ({{ bulkResults.length }} databases)</h5>
      <table class="table table-striped table-hover">
        <thead>
          <tr>
            <th style="width: 50px">
              <input type="checkbox" v-model="selectAll" @change="toggleSelectAll" />
            </th>
            <th>Database</th>
            <th>Status</th>
            <th>Changes</th>
            <th>Summary</th>
            <th style="width: 100px">Actions</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="result in bulkResults" :key="result.databaseName">
            <tr @click="toggleRowDetails(result.databaseName)" style="cursor: pointer;">
              <td @click.stop>
                <input
                  type="checkbox"
                  v-model="result.selected"
                  :disabled="!result.hasChanges || result.error"
                />
              </td>
              <td>
                <FontAwesomeIcon
                  :icon="expandedRow === result.databaseName ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-right'"
                  class="me-2"
                />
                {{ result.databaseName }}
              </td>
              <td>
                <span v-if="result.error" class="badge bg-danger">Error</span>
                <span v-else-if="result.hasChanges" class="badge bg-warning">Changes Detected</span>
                <span v-else class="badge bg-success">Up to Date</span>
              </td>
              <td>
                <span v-if="result.hasChanges" class="badge bg-info">{{ result.changeCount }}</span>
                <span v-else>-</span>
              </td>
              <td>
                <span v-if="result.error" class="text-danger">{{ result.error }}</span>
                <span v-else>{{ result.summary }}</span>
              </td>
              <td @click.stop>
                <button
                  v-if="result.hasChanges && !result.error"
                  class="btn btn-sm btn-primary"
                  @click="showBulkResultScript(result)"
                >
                  Review Script
                </button>
              </td>
            </tr>
            <!-- Detail Row -->
            <tr v-if="expandedRow === result.databaseName && result.hasChanges && !result.error">
              <td colspan="6" class="bg-light">
                <div class="p-3">
                  <div class="row">
                    <div class="col-md-3" v-if="result.tablesAddedList && result.tablesAddedList.length > 0">
                      <strong>Tables Added ({{ result.tablesAddedList.length }}):</strong>
                      <ul class="list-unstyled mt-2">
                        <li v-for="(table, idx) in result.tablesAddedList" :key="idx" class="text-muted small">
                          • {{ table }}
                        </li>
                      </ul>
                    </div>
                    <div class="col-md-3" v-if="result.tablesModifiedList && result.tablesModifiedList.length > 0">
                      <strong>Tables Modified ({{ result.tablesModifiedList.length }}):</strong>
                      <ul class="list-unstyled mt-2">
                        <li v-for="(table, idx) in result.tablesModifiedList" :key="idx" class="text-muted small">
                          • {{ table }}
                        </li>
                      </ul>
                    </div>
                    <div class="col-md-3" v-if="result.routinesAddedList && result.routinesAddedList.length > 0">
                      <strong>Routines Added ({{ result.routinesAddedList.length }}):</strong>
                      <ul class="list-unstyled mt-2">
                        <li v-for="(routine, idx) in result.routinesAddedList" :key="idx" class="text-muted small">
                          • {{ routine }}
                        </li>
                      </ul>
                    </div>
                    <div class="col-md-3" v-if="result.routinesChangedList && result.routinesChangedList.length > 0">
                      <strong>Routines Changed ({{ result.routinesChangedList.length }}):</strong>
                      <ul class="list-unstyled mt-2">
                        <li v-for="(routine, idx) in result.routinesChangedList" :key="idx" class="text-muted small">
                          • {{ routine }}
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
      <div class="mt-2">
        <button
          class="btn btn-success"
          :disabled="!hasSelectedBulkResults || isBulkApplying"
          @click="confirmBulkApply"
        >
          <FontAwesomeIcon v-if="isBulkApplying" icon="fa-solid fa-spinner me-2" spin />
          Apply to Selected ({{ selectedBulkCount }})
        </button>
      </div>
    </div>
  </div>
  <!-- Script Review for Bulk Result -->
  <div v-else-if="bulkResults.length > 0 && reviewingBulkResult" class="row mt-3">
    <div class="col">
      <button class="btn btn-secondary btn-sm mb-2" @click="closeBulkReview">
        <FontAwesomeIcon icon="fa-solid fa-arrow-left" /> Back to Results
      </button>
      <button class="btn btn-primary btn-sm mb-2 ms-2" @click="copy('script')">Copy</button>
      <span class="ms-2"><strong>{{ scriptName }}</strong></span>
      <code-diff :old-string="oldScript" :new-string="currentScript" :language="'sql'" output-format="line-by-line">
      </code-diff>
    </div>
  </div>
  <div v-if="isComparing" class="row mt-2">
    <!-- ANALYZING (wait) -->
    <div class="col">
      <FontAwesomeIcon icon="fa-solid fa-spinner me-2" spin /> Comparing...
    </div>
  </div>
  <div v-else-if="gotCompare" class="row mt-3">
    <!-- ANALYZING -->
    <div class="col">
      <div class="row custom-results">
        <div class="col">
          <span class="custom-label">Tables Added:</span>
          <ul class="custom-list">
            <li v-for="(item, idx) of compare.TablesAdded" :key="idx">
              <a href="#" @click="showScript(item, null)">{{
                item.Name
              }}</a>
            </li>
          </ul>
        </div>
        <div class="col">
          <span class="custom-label">Table(s) Columns Added:</span>
          <ul class="custom-list">
            <li v-for="(item, idx) of compare.TablesColumnsAdded" :key="idx">
              <a href="#" @click="showScript(item, null)">{{
                item.Name
              }}</a>:
              <span class="fst-italic">{{ item.Columns.length }} column(s)</span>
            </li>
          </ul>
        </div>
        <div class="col">
          <span class="custom-label">Routines Added:</span>
          <ul class="custom-list">
            <li v-for="(item, idx) of compare.RoutinesAdded" :key="idx">
              <span class="fst-italic">{{ item.Type }}</span>:
              <a href="#" @click="showScript(item, null)">{{
                item.Name
              }}</a>
            </li>
          </ul>
        </div>
        <div class="col">
          <span class="custom-label">Routines Changed:</span>
          <ul class="custom-list">
            <li v-for="(item, idx) of compare.RoutinesChanged" :key="idx">
              <span class="fst-italic">{{ item.Type }}</span>:
              <a href="#" @click="showScript(item, item.CurrentText)">{{
                item.Name
              }}</a>
            </li>
          </ul>
        </div>
        <div class="custom-json">
          <a href="#" @click="isJson = true">json</a>
        </div>
      </div>
      <div v-if="isScript" class="row">
        <!-- Routine -->
        <div v-if="isRoutine" class="col">
          <button class="btn btn-primary btn-sm" @click="copy('script')">Copy</button>
          <span class="ms-2">{{ scriptName }}</span>
          <code-diff :old-string="oldScript" :new-string="currentScript" :language="'sql'" output-format="side-by-side"
            filename="Target" new-filename="Source">
          </code-diff>
        </div>
        <!-- Full Script -->
        <div v-else-if="!isEdit" class="col">
          <button class="btn btn-primary btn-sm" @click="copy('script')">Copy</button>
          <span class="ms-2">{{ scriptName }}</span>
          <!-- <a href="#" class="ms-2" @click="edit()">edit</a> -->
          <code-diff :old-string="oldScript" :new-string="currentScript" :language="'sql'" output-format="line-by-line">
          </code-diff>
        </div>
        <!-- Editing Script -->
        <!-- <div v-else class="col">
          <a href="#" class="ms-2" @click="cancel()">cancel</a>
          <span v-if="isSave" class="ms-2 text-danger">Script Changed</span>
          <a href="#" class="ms-2" @click="save()">save</a>
          <CodeEditor
            v-model="currentScript"
            :languages="[['sql', 'SQL']]"
            :tab-spaces="4"
            class="custom-editor"
            width="100%"
            theme="github"
            @keyup="isSave = true"
          ></CodeEditor>
        </div> -->
      </div>
      <!-- View as JSON -->
      <div v-if="isJson" class="row">
        <div class="col">
          <a href="#" class="ms-2" @click="cancelJson()">cancel</a>
          <json-viewer :value="compare" copyable />
        </div>
      </div>
      <div v-if="isError" class="row mt-2">
        <div class="col">
          <div class="alert alert-danger" role="alert">
            {{ error }}
          </div>
        </div>
      </div>
    </div>
  </div>
  <confirm-apply v-model="actionConfirm" :confirm-title="confirmTitle" :confirm-action="confirmAction"
    :confirm-question="confirmQuestion" @closing="closeModal" @proceed="performApply" />
</template>
<script>
// eslint-disable-next-line no-unused-vars
import hljs from 'highlight.js'
import { mapState, mapActions } from 'pinia'
import { compareStore } from '@/stores/compare'
import { authStore } from '@/stores/auth'
import { JsonViewer } from 'vue3-json-viewer'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { CodeDiff } from 'v-code-diff'
import * as signalR from '@microsoft/signalr'
// import CodeEditor from 'simple-code-editor'
import Multiselect from '@vueform/multiselect'
import ConfirmApply from '@/components/utilities/elements/ConfirmDialog.vue'
import 'vue3-json-viewer/dist/index.css'
export default {
  name: 'CompareDatabase',
  components: {
    JsonViewer,
    FontAwesomeIcon,
    Multiselect,
    CodeDiff,
    ConfirmApply,
    // CodeEditor,
  },
  data() {
    return {
      source: null,
      target: null,
      sourceMode: 'database',
      comparisonMode: 'single', // 'single' or 'bulk' - only used when sourceMode is 'file'
      signalRConnection: null,
      isScript: false,
      isJson: false,
      selectedDBs: [],
      currentScript: null,
      oldScript: null,
      isRoutine: false,
      isEdit: false,
      isSave: false,
      isApplying: false,
      actionConfirm: null,
      confirmTitle: 'Confirm?',
      confirmAction: 'Apply',
      confirmQuestion:
        'Are you sure you wish to Apply this script to selected databases?',
      scriptName: null,
      bulkType: null,
      bulkGroup: null,
      selectAll: false,
      reviewingBulkResult: null,
      expandedRow: null,
    }
  },
  async mounted() {
    this.init()
    await this.setupSignalR()
  },
  async beforeUnmount() {
    // Cleanup temp database when leaving page
    await this.cleanupTempDatabaseHandler()

    // Disconnect SignalR
    if (this.signalRConnection) {
      await this.signalRConnection.stop()
    }
  },
  watch: {
    sourceMode(newMode, oldMode) {
      // Cleanup temp database when switching away from file mode
      if (oldMode === 'file' && newMode === 'database') {
        this.cleanupTempDatabaseHandler()
        if (this.$refs.fileInput) {
          this.$refs.fileInput.value = ''
        }
      }
      // Reset comparison mode when switching source modes
      this.comparisonMode = 'single'
    },
    comparisonMode(newMode) {
      // Reset bulk results when switching away from bulk mode
      if (newMode !== 'bulk') {
        this.resetBulkCompare()
        this.selectAll = false
      }
    },
    target(newVal, oldVal) {
      console.log('Target changed from', oldVal, 'to', newVal)
      console.log('After target change - selectedCompare:', this.selectedCompare)
      console.log('After target change - isSource:', this.isSource)
      console.log('After target change - tempDatabaseName:', this.tempDatabaseName)
    }
  },
  computed: {
    ...mapState(compareStore, [
      'loading',
      'databases',
      'compare',
      'uploadSessionId',
      'uploadedFileName',
      'tempDatabaseName',
      'uploadError',
      'bulkResults',
      'bulkError'
    ]),
    authToken() {
      const auth = authStore()
      return auth.token
    },
    isUploading() {
      return this.loading.upload
    },
    isBulkComparing() {
      return this.loading.bulkCompare
    },
    isBulkApplying() {
      return this.loading.bulkApply
    },
    isError() {
      return !!this.uploadError || !!this.bulkError
    },
    error() {
      return this.uploadError || this.bulkError
    },
    isComparing() {
      let _is = false
      if (this.loading.compare) {
        _is = this.loading.compare
      }
      return _is
    },
    gotCompare() {
      let _is = false
      if (this.compare) {
        _is = true
      }
      return _is
    },
    selectedCompare() {
      let _is = false
      // In file mode, check tempDatabaseName instead of source
      const hasSource = this.sourceMode === 'file' ? this.tempDatabaseName !== null : this.source !== null
      if (hasSource && this.target !== null) {
        _is = true
      }
      return _is
    },
    gotTargets() {
      let _is = false
      if (this.selectedDBs.length > 0) {
        _is = true
      }
      return _is
    },
    sourceDBs() {
      let _dbs = []
      if (this.databases) {
        _dbs = this.databases.filter(x => x.type === 'DEV')
      }
      return _dbs
    },
    targetDBs() {
      let _dbs = []
      if (this.databases) {
        _dbs = this.databases.filter(x => x.type === 'PROD')
      }
      return _dbs
    },
    groupDevDBs() {
      let _dbs = []
      if (this.databases) {
        let filtered = this.databases.filter(x => x.type === 'DEV')
        // Group data by the 'group' field
        const groupedData = filtered.reduce((acc, item) => {
          const label = item.group

          // If the label doesn't exist in the accumulator, create it
          if (!acc[label]) {
            acc[label] = []
          }

          // Add the database name to the appropriate group
          acc[label].push(item.databaseName)

          return acc
        }, {})
        // Convert grouped data into the desired format
        _dbs = Object.keys(groupedData).map(label => ({
          label,
          options: groupedData[label].sort(),
        }))
      }
      return _dbs
    },
    groupTargetDBs() {
      let _dbs = []

      // If in file mode, show all databases grouped (temp databases aren't in the databases list)
      if (this.sourceMode === 'file' && this.tempDatabaseName && this.databases) {
        const groupedData = this.databases.reduce((acc, item) => {
          const label = item.group
          if (!acc[label]) {
            acc[label] = []
          }
          acc[label].push(item.databaseName)
          return acc
        }, {})

        _dbs = Object.keys(groupedData).map(label => ({
          label,
          options: groupedData[label].sort(),
        }))
        return _dbs
      }

      // Otherwise, use the existing database mode filtering logic
      if (this.databases && this.source) {
        let group = null
        let found = this.databases.find(x => x.databaseName === this.source)
        if (found) {
          group = found.group
          let filtered = this.databases.filter(x => x.group === group)
          // Group data by the 'group' field
          const groupedData = filtered.reduce((acc, item) => {
            const label = item.group

            // If the label doesn't exist in the accumulator, create it
            if (!acc[label]) {
              acc[label] = []
            }

            // Add the database name to the appropriate group
            if (item.databaseName !== this.source) {
              if (this.source.indexOf('AdasMatrix') !== -1) {
                if (item.databaseName.indexOf('AdasMatrix') !== -1) {
                  acc[label].push(item.databaseName)
                }
              } else if (this.source.indexOf('LaunchPad') !== -1) {
                if (item.databaseName.indexOf('LaunchPad') !== -1) {
                  acc[label].push(item.databaseName)
                }
              } else if (this.source.indexOf('ConnectCopilot') !== -1) {
                if (item.databaseName.indexOf('ConnectCopilot') !== -1) {
                  acc[label].push(item.databaseName)
                }
              } else if (this.source.indexOf('ConnectRollup') !== -1) {
                if (item.databaseName.indexOf('ConnectRollup') !== -1) {
                  acc[label].push(item.databaseName)
                }
              } else {
                acc[label].push(item.databaseName)
              }
            }

            return acc
          }, {})
          // Convert grouped data into the desired format
          _dbs = Object.keys(groupedData).map(label => ({
            label,
            options: groupedData[label].sort(),
          }))
        }
      }
      return _dbs
    },
    groupDBs() {
      let _dbs = []
      if (this.databases) {
        // Group data by the 'group' field
        const groupedData = this.databases.reduce((acc, item) => {
          const label = item.group

          // If the label doesn't exist in the accumulator, create it
          if (!acc[label]) {
            acc[label] = []
          }

          // Add the database name to the appropriate group
          acc[label].push(item.databaseName)

          return acc
        }, {})

        // Convert grouped data into the desired format
        _dbs = Object.keys(groupedData).map(label => ({
          label,
          options: groupedData[label].sort(),
        }))
      }
      return _dbs
    },
    isSource() {
      let _is = false
      if (this.sourceMode === 'database' && this.source) {
        _is = true
      } else if (this.sourceMode === 'file' && this.tempDatabaseName) {
        _is = true
      }
      return _is
    },
    isTarget() {
      let _is = false
      if (this.target) {
        _is = true
      }
      return _is
    },
    script() {
      let _script = ''
      if (this.compare) {
        _script = this.compare.Script
      }
      return _script
    },
    currentText() {
      let _script = ''
      if (this.compare) {
        _script = this.compare.currentText
      }
      return _script
    },
    uniqueTypes() {
      if (!this.databases) return []
      const types = [...new Set(this.databases.map(db => db.type).filter(Boolean))]
      return types.sort()
    },
    uniqueGroups() {
      if (!this.databases) return []
      const groups = [...new Set(this.databases.map(db => db.group).filter(Boolean))]
      return groups.sort()
    },
    hasSelectedBulkResults() {
      return this.bulkResults.some(r => r.selected)
    },
    selectedBulkCount() {
      return this.bulkResults.filter(r => r.selected).length
    },
  },
  methods: {
    ...mapActions(compareStore, [
      'resetCompare',
      'resetUpload',
      'resetBulkCompare',
      'getDatabases',
      'getCompare',
      'applyScript',
      'uploadDatabaseFile',
      'cleanupTempDatabase',
      'handleUploadComplete',
      'compareBulk',
      'applyBulkScript',
    ]),
    init() {
      this.reset('all')
      this.getDatabases()
    },
    async setupSignalR() {
      try {
        const apiUrl = window.config ? window.config.apiUrl : ''
        const hubUrl = `${apiUrl}/servicehub`

        this.signalRConnection = new signalR.HubConnectionBuilder()
          .withUrl(hubUrl, {
            accessTokenFactory: () => this.authToken
          })
          .withAutomaticReconnect()
          .build()

        // Listen for database upload completion
        this.signalRConnection.on('DatabaseUploadComplete', (data) => {
          console.log('DatabaseUploadComplete received:', data)

          // Check if this message is for our upload session
          if (data.sessionId === this.uploadSessionId) {
            // Update store with upload completion data
            this.handleUploadComplete(data)

            if (data.success) {
              console.log('Database created successfully:', data.tempDatabaseName)
              console.log('tempDatabaseName set to:', this.tempDatabaseName)
              console.log('sourceMode:', this.sourceMode)
              console.log('isSource computed:', this.isSource)
              console.log('target:', this.target)
              console.log('selectedCompare computed:', this.selectedCompare)

              // Force Vue to update computed properties
              this.$forceUpdate()
            } else {
              // Clear file input on error
              if (this.$refs.fileInput) {
                this.$refs.fileInput.value = ''
              }
            }
          }
        })

        await this.signalRConnection.start()
        console.log('SignalR connected')
      } catch (error) {
        console.error('SignalR connection error:', error)
      }
    },
    reset(from) {
      this.isSave = false
      this.isEdit = false
      this.isRoutine = false
      this.isScript = false
      this.isJson = false
      this.isApplying = false
      this.target = null
      this.isError = false
      this.error = null
      if (from === 'all' || from === 'source') {
        this.source = null
      }
      this.selectedDBs = []
      this.currentScript = null
      this.oldScript = null
      this.resetCompare()
    },
    async compareDBs() {
      const sourceDb = this.sourceMode === 'file' ? this.tempDatabaseName : this.source

      console.log('Comparing DBs', {
        source: sourceDb,
        target: this.target,
        mode: this.sourceMode
      })
      this.isSave = false
      this.isEdit = false
      this.isRoutine = false
      this.isScript = false
      this.isJson = false
      this.isApplying = false
      this.currentScript = null
      this.oldScript = null
      this.isError = false
      this.error = null
      await this.getCompare({
        source: sourceDb,
        target: this.target,
        action: 'analyze',
      })
    },
    async performBulkCompare() {
      console.log('Bulk comparison starting', {
        template: this.tempDatabaseName,
        type: this.bulkType,
        group: this.bulkGroup
      })

      const result = await this.compareBulk({
        templateDatabase: this.tempDatabaseName,
        type: this.bulkType,
        group: this.bulkGroup
      })

      if (result.success) {
        console.log('Bulk comparison results:', result.results)
      } else {
        console.error('Bulk comparison error:', result.error)
      }
    },
    toggleSelectAll() {
      const newValue = this.selectAll
      this.bulkResults.forEach(result => {
        if (result.hasChanges && !result.error) {
          result.selected = newValue
        }
      })
    },
    showBulkResultScript(result) {
      this.reviewingBulkResult = result
      this.isScript = true
      this.isRoutine = false
      this.oldScript = null
      this.isEdit = false
      this.scriptName = `Changes for ${result.databaseName}`
      this.currentScript = result.script
    },
    closeBulkReview() {
      this.isScript = false
      this.reviewingBulkResult = null
      this.currentScript = null
      this.scriptName = null
    },
    toggleRowDetails(databaseName) {
      if (this.expandedRow === databaseName) {
        this.expandedRow = null
      } else {
        this.expandedRow = databaseName
      }
    },
    confirmBulkApply() {
      this.confirmTitle = 'Apply Changes to Multiple Databases?'
      this.confirmAction = 'Apply'
      this.confirmQuestion = `Are you sure you want to apply changes to ${this.selectedBulkCount} database(s)?`
      this.actionConfirm = 'bulk'
    },
    async performBulkApply() {
      this.actionConfirm = null

      const selectedResults = this.bulkResults.filter(r => r.selected)
      console.log(`Applying to ${selectedResults.length} databases`)

      const result = await this.applyBulkScript(selectedResults)

      // Show results
      if (result.success) {
        alert(`Successfully applied changes to ${result.successCount} database(s)`)
        // Clear bulk results and reset after successful apply
        this.resetBulkCompare()
        this.selectAll = false
      } else {
        const errorMsg = result.errors.length > 0
          ? `Applied to ${result.successCount} database(s). Errors:\n${result.errors.join('\n')}`
          : 'Failed to apply changes'
        alert(errorMsg)
      }
    },
    async handleFileUpload(event) {
      const file = event.target.files[0]
      if (!file) return

      // Call store action to upload the file
      const result = await this.uploadDatabaseFile(file)

      if (result.success) {
        console.log('Upload started, waiting for database creation...', result)
        // Database creation will complete via SignalR callback
      } else {
        console.error('Upload error:', result.error)
        this.$refs.fileInput.value = ''
      }
    },
    async cleanupTempDatabaseHandler() {
      if (!this.tempDatabaseName) return

      await this.cleanupTempDatabase(this.tempDatabaseName)
      this.resetUpload()
    },
    closeModal() {
      this.actionConfirm = null
    },
    showFullScript() {
      this.isScript = true
      this.isRoutine = false
      this.oldScript = null
      this.isEdit = false
      this.scriptName = 'Full Script'
      this.currentScript = this.compare.Script
    },
    showScript(item, oldScript) {
      console.log('Show Script', item)
      this.isEdit = false
      this.isRoutine = false
      this.currentScript = item.Text
      this.oldScript = null
      this.scriptName = item.Name
      if (oldScript) {
        this.isRoutine = true
        this.oldScript = oldScript
      }
      this.isScript = true
    },
    copy() {
      navigator.clipboard.writeText(this.currentScript)
      alert('Script copied to clipboard')
    },
    edit() {
      this.isEdit = true
    },
    cancel() {
      this.isEdit = false
      this.isSave = false
      this.currentScript = this.script
    },
    cancelJson() {
      this.isJson = false
    },
    save() {
      this.script = this.currentScript
      this.isEdit = false
      this.isSave = false
    },
    confirmApply() {
      this.confirmTitle = 'Confirm?'
      this.confirmAction = 'Apply'
      this.confirmQuestion = 'Are you sure you wish to Apply this script to selected databases?'
      this.actionConfirm = 'regular'
    },
    async performApply() {
      // Check if this is bulk apply or regular apply
      if (this.actionConfirm === 'bulk') {
        await this.performBulkApply()
        return
      }

      // Regular apply logic
      this.isError = false
      this.error = null
      let data = {
        script: this.currentScript ? this.currentScript : this.script,
        databases: this.selectedDBs,
      }
      this.actionConfirm = null
      console.log('Applying to Targets', data)
      this.isApplying = true
      let results = await this.applyScript(data)
      this.isApplying = false
      if (results.indexOf('error') !== -1) {
        console.log('Error', results)
        this.isScript = false
        this.error = results
        this.isError = true
      } else {
        alert('Script applied to selected databases')
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.custom-group {
  height: 54px;
}

.custom-separator {
  border-left: 2px solid #ffffff;
}

.custom-label {
  font-weight: bold;
}

.multiselect-dropdown {
  --ms-max-height: 20rem;
}

.custom-list {
  margin-top: 5px;
  list-style-type: none;
  padding: 0;
  overflow-x: hidden;
  overflow-y: auto;
  max-height: 300px;
}

.custom-editor {
  border: 1px solid #ced4da;
}

.custom-results {
  position: relative;
}

.custom-json {
  position: absolute;
  right: 0;
  top: 0;
  width: 100px;
  text-align: right;
}
</style>
